import { useEffect, useMemo, useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import axiosInstance from "@/lib/axios";
import { dashboardFilterOptions } from "@/config/endpoints";
import type { DropdownOption } from "../ui/FilterDropdown";
import { useDashboardV2Filters } from "../state/DashboardV2Context";
import { useDashboardBranchFilterHidden } from "@/features/leads/api/useLeadFormConfig";

/**
 * Cascade-aware, RBAC-scoped options for the dashboard filter bar (Country /
 * Branch / Role / User). One call to `/core/dashboard/filter-options` with the
 * CURRENT selections → the backend returns interconnected lists (picking a
 * Branch/Role narrows Users; Branch narrows Roles; Country re-scopes per the
 * selected user/role's CF reach). The viewer's RBAC ceiling is enforced
 * server-side, so a counsellor can't "view-as" someone above their scope.
 *
 * Also self-heals: when a selection falls out of the freshly-narrowed list
 * (e.g. the chosen user isn't in the picked branch), it resets that pill to
 * "all" so the bar never shows a stale, invalid selection.
 */
interface FilterOptionsResponse {
  branches: { id: number; name: string }[];
  roles: { id: number; name: string }[];
  users: { id: number; name: string; roleId: number | null; branchIds: number[] }[];
  countries: { id: number; name: string }[] | null;
  applied: { branchId: number[]; roleId: number | null; userId: number | null; countryItemId: number | null };
  /**
   * Server-chosen DEFAULT for the widget role pills: the most senior (lowest
   * `hierarchy`) PERSONAL-scope role the viewer may see, picked from the SAME
   * `roles` list above and already respecting the role-wizard exclusions and
   * rank rules. IDENTIFICATION ONLY — the server applies nothing, so the pill
   * must visibly show it (the "a pill never lies about its numbers" rule).
   */
  defaultPersonalRoleId?: number | null;
  /**
   * Server-chosen seed for the FOLLOW-UP pills only (rail / by-user /
   * by-branch), honouring Settings → Manage "Dashboard default role":
   * "auto" → same as `defaultPersonalRoleId`; "current_role" → the viewer's
   * OWN role. Absent on an older server → consumers fall back to
   * `defaultPersonalRoleId`.
   */
  followUpDefaultRoleId?: number | null;
  followUpDefaultRoleMode?: "auto" | "current_role";
  /** Server-computed: does this viewer see anyone's work but their own? */
  viewerScope?: {
    isManagerial?: boolean;
    personalScope?: boolean;
    followUpOwnStageIds?: number[];
    followUpRoleStages?: Record<string, number[]>;
  };
}

export interface V2FilterOptions {
  branchOptions: DropdownOption[];
  /**
   * Role list WITH the "All roles" entry — for the GLOBAL top-bar "view as"
   * pill only, where "no narrowing" is a legitimate scope.
   */
  roleOptions: DropdownOption[];
  /**
   * Role list WITHOUT "All roles" — for the WIDGET role filters, which must
   * always carry a concrete role (user directive 2026-09-01: "no all filters
   * should be allowed for the role filter … in dashboards must be selected
   * some role"). Empty when the viewer may see no role at all, in which case
   * the consuming pill hides and the widget stays unnarrowed rather than
   * sending a bogus id.
   */
  roleOptionsNoAll: DropdownOption[];
  userOptions: DropdownOption[];
  countryOptions: DropdownOption[];
  /** true when the tenant has a Country CF (study-abroad) → show the dropdown. */
  countryEnabled: boolean;
  /**
   * The viewer sees other people's work (admin, admin/manage stages, or
   * CF/Track-Manage reach). Computed SERVER-side by `isManagerialViewer` —
   * never re-derive it here from `accessConfig`, or reach-based roles
   * (Country Head) get misread as personal-scoped.
   *
   * False for a personal-scoped person, whose data is their own no matter
   * what any pill says — so the Role / User "view as" pills are hidden for
   * them rather than left to filter their own work down to nothing.
   */
  isManagerialViewer: boolean;
  /**
   * THREE-TIER STAGE SCOPE (server-computed, NOT `isManagerialViewer`): a
   * stage at `admin` = the whole tenant incl. branchless; `manage` = the
   * holder's branches; `edit`/`view` = PERSONAL scope. True when the viewer
   * holds NO admin/manage stage (and isn't a tenant admin). Every follow-up
   * ROLE dropdown (rail / by-user / by-branch / drawer slot picker) gates on
   * this: a personal-scoped viewer's role filter IS their own role, whatever
   * CF/Track reach their role carries — reach widens lead visibility for the
   * "view as" pills, never whose follow-ups they supervise.
   */
  isPersonalScopeViewer: boolean;
  /**
   * Stage ids where the viewer's own role OWNS the follow-up slot (the
   * server's `actorOwnsSlot` write rule). While the open entity sits in one
   * of these, a follow-up write can only land in their own slot — the drawer
   * must not offer a "whose follow-up" picker (a foreign pick would 403).
   * Empty for admins/privileged viewers, who never own a slot.
   */
  followUpOwnStageIds: number[];
  /**
   * WHICH ROLES DO FOLLOW-UPS WHERE (server `participatesOnStage`): roleId →
   * stage ids the role participates on. The drawer's "whose follow-up"
   * picker filters an entity's assigned roles through it so it never offers
   * a slot the server rejects, and explains when none is offerable. `null`
   * while loading (or from an older server) → consumers don't filter.
   */
  followUpRoleStages: Record<string, number[]> | null;
  /**
   * Seed for every WIDGET role pill: the most senior personal-scope role this
   * viewer may see, chosen SERVER-side from `roleOptionsNoAll`'s own list
   * (exclusions + rank already applied — never re-filter it here). `null`
   * while loading, or when the viewer has no personal-scope role, in which
   * case consumers fall back to the first available option.
   */
  defaultPersonalRoleId: number | null;
  /**
   * Seed for the FOLLOW-UP role pills (rail, Follow-ups by user, Follow-ups
   * by branch) — the Manage "Dashboard default role" setting applied
   * server-side: "auto" = `defaultPersonalRoleId`, "current_role" = the
   * viewer's own role. `null` while loading or from an older server, in
   * which case the follow-up pills fall back to `defaultPersonalRoleId`.
   * UserRanking / IntakeInsights never read this — they keep the personal
   * seed.
   */
  followUpDefaultRoleId: number | null;
  /**
   * The raw visible user roster — `id`, `name`, `roleId`, `branchIds` — for
   * widgets that need to CASCADE a user picker off their own local role pill
   * (the Follow-ups rail). `userOptions` above is the flat global list; this
   * is the same people with the role attached so a widget can offer only the
   * holders of the role it is currently showing. Already bounded by the
   * viewer's RBAC ceiling server-side — filtering it further only narrows.
   */
  users: { id: number; name: string; roleId: number | null; branchIds: number[] }[];
  isLoading: boolean;
}

/** Stable empty roster so `users` never changes identity while loading. */
const EMPTY_USERS: V2FilterOptions["users"] = [];

const numeric = (v: string | null | undefined): string | undefined =>
  v && v !== "all" && /^\d+$/.test(v) ? v : undefined;

export function useV2FilterOptions(): V2FilterOptions {
  const { branch, role, user, country, globalCf, setBranch, setRole, setUser, setCountry } =
    useDashboardV2Filters();

  // Settings → Manage "Hide branch filters": with no Branch dropdown on screen a
  // leftover pick must not keep narrowing the dashboard (or its Role/User
  // lists) with no way to clear it — reset it, and never cascade on it.
  const branchHidden = useDashboardBranchFilterHidden();
  useEffect(() => {
    if (branchHidden && branch !== "all") setBranch("all");
  }, [branchHidden, branch, setBranch]);

  // Debounce the active selections so rapid dropdown changes don't fan out.
  const branchId = branchHidden ? undefined : numeric(branch);
  const roleId = numeric(role);
  const userId = numeric(user);
  const countryItemId = numeric(country);
  // The key-field pills (a Team): the server keeps only that team's people in
  // the User list, as it does for the Country pill.
  const cfKey = Object.entries(globalCf ?? {})
    .filter(([, v]) => numeric(v))
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([k, v]) => `${k}=${v}`)
    .join("&");
  const [params, setParams] = useState<Record<string, string | undefined>>({
    branchId,
    roleId,
    userId,
    countryItemId,
  });
  useEffect(() => {
    const t = setTimeout(() => {
      const cf: Record<string, string> = {};
      for (const pair of cfKey ? cfKey.split("&") : []) {
        const [k, v] = pair.split("=");
        cf[`cf_${k}`] = v!;
      }
      setParams({ branchId, roleId, userId, countryItemId, ...cf });
    }, 200);
    return () => clearTimeout(t);
  }, [branchId, roleId, userId, countryItemId, cfKey]);

  const query = useQuery({
    queryKey: ["v2-filter-options", params],
    queryFn: async (): Promise<FilterOptionsResponse> => {
      const res = await axiosInstance.get(dashboardFilterOptions, { params });
      return res.data?.data as FilterOptionsResponse;
    },
    placeholderData: keepPreviousData,
    staleTime: 60_000,
  });
  const data = query.data;

  // Self-heal: drop any current selection that's no longer in its cascaded list.
  // Resetting to "all" only ever broadens, so this can't loop.
  useEffect(() => {
    if (!data) return;
    if (user !== "all" && /^\d+$/.test(user) && !data.users.some((u) => String(u.id) === user))
      setUser("all");
    if (role !== "all" && /^\d+$/.test(role) && !data.roles.some((r) => String(r.id) === role))
      setRole("all");
    if (
      country !== "all" &&
      /^\d+$/.test(country) &&
      data.countries != null &&
      !data.countries.some((c) => String(c.id) === country)
    )
      setCountry("all");
    if (branch !== "all" && /^\d+$/.test(branch) && !data.branches.some((b) => String(b.id) === branch))
      setBranch("all");
  }, [data, user, role, country, branch, setUser, setRole, setCountry, setBranch]);

  const branchOptions = useMemo<DropdownOption[]>(
    () => [
      { key: "all", label: "All branches" },
      ...(data?.branches ?? []).map((b) => ({ key: String(b.id), label: b.name })),
    ],
    [data?.branches],
  );
  // The payload's role list is ALREADY exclusion-filtered server-side (role
  // wizard exclusions + rank rules) — never re-filter it here.
  const roleOptionsNoAll = useMemo<DropdownOption[]>(
    () => (data?.roles ?? []).map((r) => ({ key: String(r.id), label: r.name })),
    [data?.roles],
  );
  // Global "view as" pill only — the widget pills use `roleOptionsNoAll`.
  const roleOptions = useMemo<DropdownOption[]>(
    () => [{ key: "all", label: "All roles" }, ...roleOptionsNoAll],
    [roleOptionsNoAll],
  );
  const userOptions = useMemo<DropdownOption[]>(
    () => [
      { key: "all", label: "All users" },
      ...(data?.users ?? []).map((u) => ({ key: String(u.id), label: u.name })),
    ],
    [data?.users],
  );
  const countryOptions = useMemo<DropdownOption[]>(
    () => [
      { key: "all", label: "All countries" },
      ...(data?.countries ?? []).map((c) => ({ key: String(c.id), label: c.name })),
    ],
    [data?.countries],
  );

  // Stable reference so consumers can put it in memo/effect deps.
  const followUpOwnStageIds = useMemo<number[]>(
    () => data?.viewerScope?.followUpOwnStageIds ?? [],
    [data],
  );
  const followUpRoleStages = useMemo<Record<string, number[]> | null>(
    () => data?.viewerScope?.followUpRoleStages ?? null,
    [data],
  );

  return {
    branchOptions,
    roleOptions,
    roleOptionsNoAll,
    userOptions,
    users: data?.users ?? EMPTY_USERS,
    countryOptions,
    countryEnabled: data?.countries != null,
    // Defaults to FALSE while loading: hiding a pill for a moment is harmless,
    // whereas showing one that then vanishes is a flicker — and nothing here
    // gates data, only controls.
    isManagerialViewer: data?.viewerScope?.isManagerial === true,
    isPersonalScopeViewer: data?.viewerScope?.personalScope === true,
    followUpOwnStageIds,
    followUpRoleStages,
    defaultPersonalRoleId: data?.defaultPersonalRoleId ?? null,
    followUpDefaultRoleId: data?.followUpDefaultRoleId ?? null,
    isLoading: query.isLoading,
  };
}
